// @ts-nocheck
import React, { useState, useEffect } from 'react';
import SlideOverPanel from './SlideOverPanel';
import { DEFAULT_COPY_ANALYSIS_STEPS, type CopyAnalysisStepOptions } from '../../../services/AnalysisApiClient';

type Mode = 'add' | 'edit' | 'copy';

const COPY_STEP_ROWS: { key: keyof CopyAnalysisStepOptions; label: string; description: string }[] = [
  { key: 'transaction', label: 'Transaction', description: 'Deal price, AFR, headcount' },
  { key: 'individuals', label: 'Individuals', description: 'Disqualified individuals list' },
  { key: 'compensation', label: 'Compensation', description: 'Salary and benefits inputs' },
  { key: 'baseAmount', label: 'Base amount', description: 'W-2 and base amount data' },
  { key: 'parachute', label: 'Parachute payments', description: 'Severance and other parachute steps' },
];

export interface AddEditAnalysisPanelProps {
  isOpen: boolean;
  mode: Mode;
  initialName: string;
  /** Shown in copy mode to identify the source analysis. */
  copySourceLabel?: string;
  /** Edit mode: when the analysis is already final, the "Mark as final" checkbox starts checked. */
  editInitiallyFinal?: boolean;
  onClose: () => void;
  /**
   * Add: `(name)`.
   * Copy: `(name, copyOptions)`.
   * Edit: `(name, { markFinal })` — when `markFinal` is true, parent should lock the analysis.
   */
  onSave: (name: string, copyOptionsOrEdit?: CopyAnalysisStepOptions | { markFinal?: boolean }) => void;
}

export default function AddEditAnalysisPanel({
  isOpen,
  mode,
  initialName,
  copySourceLabel,
  editInitiallyFinal = false,
  onClose,
  onSave,
}: AddEditAnalysisPanelProps) {
  const [value, setValue] = useState(initialName);
  const [markFinal, setMarkFinal] = useState(false);
  const [copySteps, setCopySteps] = useState<CopyAnalysisStepOptions>(() => ({ ...DEFAULT_COPY_ANALYSIS_STEPS }));

  useEffect(() => {
    if (!isOpen) return;
    setValue(initialName);
    setMarkFinal(mode === 'edit' && editInitiallyFinal);
    if (mode === 'copy') {
      setCopySteps({ ...DEFAULT_COPY_ANALYSIS_STEPS });
    }
  }, [isOpen, initialName, mode, editInitiallyFinal]);

  const handleSave = () => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    if (mode === 'edit' && (markFinal || editInitiallyFinal)) {
      const result = onSave(trimmed, { markFinal });
      if (result != null && typeof result.then === 'function') {
        result.then(() => onClose()).catch(() => {});
        return;
      }
      onClose();
      return;
    }
    if (mode === 'copy') {
      onSave(trimmed, { ...copySteps });
    } else if (mode === 'edit') {
      onSave(trimmed, { markFinal });
    } else {
      onSave(trimmed);
    }
    onClose();
  };

  const toggleCopyStep = (key: keyof CopyAnalysisStepOptions) => {
    setCopySteps((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') onClose();
  };

  if (!isOpen) return null;

  const title = mode === 'add' ? 'Add analysis' : mode === 'copy' ? 'Copy analysis' : 'Edit analysis';
  const panelWidthClass = mode === 'copy' ? 'max-w-lg' : 'max-w-md';

  return (
    <SlideOverPanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      widthClass={panelWidthClass}
      animationClass="add-edit-panel"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2.5 text-sm font-medium btn-primary rounded-md transition-colors focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2"
          >
            {mode === 'copy' ? 'Create copy' : 'Save'}
          </button>
        </>
      }
    >
      {mode === 'copy' && copySourceLabel ? (
        <p className="text-sm text-gray-600">
          Copying from <span className="font-medium text-gray-800">{copySourceLabel}</span>.{' '}
          <span className="text-gray-700">
            280G run history is not copied — past runs remain only on the original analysis.
          </span>
        </p>
      ) : null}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
          placeholder="Enter analysis name"
          autoFocus
        />
      </div>
      {mode === 'edit' ? (
        <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 bg-gray-50/80 p-3">
          <input
            type="checkbox"
            checked={markFinal}
            onChange={(e) => setMarkFinal(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600"
          />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-gray-800">Mark analysis as final</span>
            <span className="block text-xs text-gray-500">
              Locks workflow edits and new 280G runs. Use a copy to make further changes.
            </span>
          </span>
        </label>
      ) : null}
      {mode === 'copy' ? (
        <fieldset className="rounded-lg border border-gray-200 bg-gray-50/80 mt-7 pt-3 px-3 pb-3 space-y-2">
          <legend className="text-xs font-medium text-gray-500 uppercase tracking-wide px-1">Steps to copy</legend>
          <p className="text-xs text-gray-500 px-1 pb-1 -mt-1">
            Run history is excluded — it is not part of this duplicate.
          </p>
          {COPY_STEP_ROWS.map(({ key, label, description }) => (
            <label key={key} className="flex items-start gap-3 cursor-pointer rounded-md p-2 hover:bg-white/80">
              <input
                type="checkbox"
                checked={copySteps[key]}
                onChange={() => toggleCopyStep(key)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-800">{label}</span>
                <span className="block text-xs text-gray-500">{description}</span>
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}
    </SlideOverPanel>
  );
}
